import { format, isValid, parse } from "date-fns";
import { ru } from "date-fns/locale";
import type { TourFilter } from "../api/ToursApi";

// Заголовок карточки строится из фильтра прогона и доступен даже в pending,
// когда сгенерированного поста ещё нет. Пример:
//   "Казахстан → Турция · 1–30 июня · прямой"
//   "Казахстан → +13 стран · 1–31 июля · до 3 000 000 ₸"

// Русская плюрализация для слова «страна».
function pluralCountries(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "страна";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "страны";
  return "стран";
}

// Разделители тысяч обычным пробелом (детерминированно, без зависимостей от локали Intl).
function formatPrice(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// Компактная дата "YYYYMMDD" -> Date | null.
function parseCompactDate(value: string): Date | null {
  if (!value) return null;
  const parsed = parse(value, "yyyyMMdd", new Date());
  return isValid(parsed) ? parsed : null;
}

function formatDateRange(from: Date | null, to: Date | null): string {
  if (from && to) {
    const sameMonth =
      from.getFullYear() === to.getFullYear() &&
      from.getMonth() === to.getMonth();
    if (sameMonth) {
      return `${format(from, "d")}–${format(to, "d MMMM", { locale: ru })}`;
    }
    return `${format(from, "d MMMM", { locale: ru })} – ${format(to, "d MMMM", {
      locale: ru,
    })}`;
  }
  if (from) return `с ${format(from, "d MMMM", { locale: ru })}`;
  if (to) return `до ${format(to, "d MMMM", { locale: ru })}`;
  return "";
}

function uniqueInOrder(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function formatRoute(filter: TourFilter): string {
  const from = uniqueInOrder(
    filter.trips.flatMap((t) => t.fromCountries)
  ).join(", ");
  const toCountries = uniqueInOrder(filter.trips.flatMap((t) => t.toCountries));

  const to =
    toCountries.length <= 2
      ? toCountries.join(", ")
      : `+${toCountries.length} ${pluralCountries(toCountries.length)}`;

  if (!from && !to) return "";
  if (!to) return from;
  if (!from) return to;
  return `${from} → ${to}`;
}

function formatFlags(filter: TourFilter): string[] {
  const parts: string[] = [];
  if (filter.is_direct) parts.push("прямой");
  if (filter.is_round_trip) parts.push("туда-обратно");
  return parts;
}

function formatPriceRange(price: TourFilter["price"]): string {
  const hasFrom = price.from > 0;
  const hasTo = price.to > 0;
  if (hasFrom && hasTo) {
    return `от ${formatPrice(price.from)} до ${formatPrice(price.to)} ₸`;
  }
  if (hasTo) return `до ${formatPrice(price.to)} ₸`;
  if (hasFrom) return `от ${formatPrice(price.from)} ₸`;
  return "";
}

export function getFilterParts(filter: TourFilter): {
  from: string;
  toCountries: string[];
  hasOverflow: boolean;
  overflowText: string;
  suffix: string;
} {
  const from = uniqueInOrder(
    filter.trips.flatMap((t) => t.fromCountries)
  ).join(", ");
  const toCountries = uniqueInOrder(filter.trips.flatMap((t) => t.toCountries));
  const hasOverflow = toCountries.length > 2;

  const parts: string[] = [];
  const dates = formatDateRange(
    parseCompactDate(filter.date.from),
    parseCompactDate(filter.date.to)
  );
  if (dates) parts.push(dates);
  parts.push(...formatFlags(filter));
  const price = formatPriceRange(filter.price);
  if (price) parts.push(price);

  return {
    from,
    toCountries,
    hasOverflow,
    overflowText: hasOverflow
      ? `+${toCountries.length} ${pluralCountries(toCountries.length)}`
      : "",
    suffix: parts.join(" · "),
  };
}

export function formatFilterLine(filter: TourFilter): string {
  const parts: string[] = [];

  const route = formatRoute(filter);
  if (route) parts.push(route);

  const dates = formatDateRange(
    parseCompactDate(filter.date.from),
    parseCompactDate(filter.date.to)
  );
  if (dates) parts.push(dates);

  parts.push(...formatFlags(filter));

  const price = formatPriceRange(filter.price);
  if (price) parts.push(price);

  return parts.join(" · ");
}
